import React, { useEffect, useState } from 'react';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
    Modal,
    Button,
    Box,
    Chip,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Stack,
    Pagination,
    Autocomplete,
    TextField,
    useMediaQuery,
} from '@mui/material';
import { ManageAccounts, Add, LockReset, ToggleOn, ToggleOff, Delete, PhonelinkLock, UploadFile, AccountBalance, MoreVert, SpaceDashboard } from '@mui/icons-material';
import { IInstitution } from '../../interfaces/institution.interface';
import { IRoleSummary } from '../../interfaces/role.interface';
import { AuthService } from '../../services/auth.service';
import { displayUsername } from '../../utils/userUtils';
import { GroupAccessService } from '../../services/groupAccess.service';
import ChangeRolesDialog from './ChangeRolesDialog';
import EmployeeBankingDialog from './EmployeeBankingDialog';
import ScrollableTable from '../common/ScrollableTable';
import SeatMeter from './SeatMeter';
import BulkOnboardingDialog from './BulkOnboardingDialog';
import { usePlan } from '../../context/PlanContext';
import { usePermission, useIsAdmin } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { helpElement } from '../../help/attrs';

interface User {
    id: string;
    username: string;
    phone?: string | null;
    roles: IRoleSummary[];
    isActive?: boolean | null;
    fullName?: string | null;
    /** Group-company grantee: home is another company, which owns the profile (server withholds phone/status). */
    grantedFromInstitutionId?: string | null;
    grantedFromInstitutionCode?: string | null;
    grantedFromInstitutionName?: string | null;
    grantMembershipId?: string | null;
}

const isGrantee = (u: User) => !!u.grantedFromInstitutionId;

const UserList = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState<IInstitution | null>(null);
    const [isChangeRolesOpen, setIsChangeRolesOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isTwoFactorResetModalOpen, setIsTwoFactorResetModalOpen] = useState(false);
    const [isBulkOnboardingOpen, setIsBulkOnboardingOpen] = useState(false);
    const [isBankingDialogOpen, setIsBankingDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    // Set by the Create User page so the new account is what the admin sees on landing.
    const createdUsername = (location.state as { createdUsername?: string } | null)?.createdUsername;
    const [toggleTarget, setToggleTarget] = useState<User | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<User | null>(null);
    const isNarrow = useMediaQuery('(max-width:600px)');
    const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; user: User } | null>(null);
    const { isAuthenticated, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const { refresh: refreshPlan } = usePlan();
    const isSystemAdmin = AuthService.getCurrentUser()?.roles === 'SystemAdmin';
    const rolesInstitutionId = selectedInstitution?.id ?? AuthService.getSelectedInstitution();
    // Fix-round-1 L-4/L-5: the button used to render unconditionally (the server rejects
    // correctly, so this was UX noise, not a security gap) and the dialog could be opened with an
    // empty institutionId, sending requests to ".../institution-users//bulk". Gate both on holding
    // USER_MANAGE AND having a resolved institution.
    const canBulkImport = usePermission(PERMISSIONS.USER_MANAGE) && !!rolesInstitutionId;
    const canManageProfile = usePermission(PERMISSIONS.PROFILE_MANAGE);
    // Row/toolbar actions are guarded server-side by USER_MANAGE (create / toggle / reset password),
    // TWO_FACTOR_ADMIN (2FA reset) and institution-admin (role changes) -- show each only to a
    // caller who can actually use it, so a USER_VIEW-only or HR Manager caller never hits a 403.
    const canManageUsers = usePermission(PERMISSIONS.USER_MANAGE);
    const canManageBoardsAll = usePermission(PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_ALL);
    const canManageBoardsTeam = usePermission(PERMISSIONS.DASHBOARD_LAYOUT_MANAGE_TEAM);
    const canManageBoards = canManageBoardsAll || canManageBoardsTeam;
    const canReset2fa = usePermission(PERMISSIONS.TWO_FACTOR_ADMIN);
    const canChangeRoles = useIsAdmin();
    // PlanContext resolves the plan for `selectedInstitutionId` (the auth-selected institution).
    // A SystemAdmin can filter this list by a DIFFERENT institution; showing the meter then would
    // describe the wrong institution, so it's hidden whenever the filter diverges.
    const seatMeterMatchesViewedInstitution = !selectedInstitution || selectedInstitution.id === selectedInstitutionId;
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });
    const [page,] = useState(1);
    const [searchTerm, setSearchTerm] = useState(createdUsername ?? '');
    const [debouncedSearch, setDebouncedSearch] = useState(createdUsername ?? '');

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        if (isSystemAdmin) {
            fetchInstitutions();
        }
    }, [isAuthenticated, navigate, page]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchInstitutionUsers(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitution, debouncedSearch, isAuthenticated]);

    const fetchInstitutions = async () => {
        try {
            const response = await InstitutionService.getInstitutions(0, 20);
            setInstitutions(response.content || []);
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load institutions');
        }
    };

    const fetchInstitutionUsers = async (page: number = 0) => {
        try {
            const response = await UserService.getInstitutionUsers(page, 10, selectedInstitution?.id, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (user: User) => {
        try {
            await UserService.toggleUserActive(user.id);
            toast.showSuccess(`User ${user.isActive !== false ? 'deactivated' : 'activated'} successfully`);
            await fetchInstitutionUsers(pageInfo.currentPage);
            // M1: deactivating/reactivating a user changes seat consumption on a FREE institution
            // (deactivation frees a seat, reactivation can consume the last one) — the meter must
            // not keep showing the pre-toggle count.
            refreshPlan();
        } catch (error: any) {
            toast.showApiError(error, 'Failed to update user status');
        }
    };

    const handleOpenDeleteModal = (user: User) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await UserService.deleteUser(selectedUser.id);
            toast.showSuccess('User deleted successfully');
            await fetchInstitutionUsers(pageInfo.currentPage);
            // M1: deleting a user frees the seat it held — the meter must not keep showing the
            // pre-delete count next to a list that has already shrunk.
            refreshPlan();
            handleCloseDeleteModal();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            toast.showApiError(error, 'Failed to delete user');
            handleCloseDeleteModal();
        }
    };

    const handleOpenChangeRolesDialog = (user: User) => {
        setSelectedUser(user);
        setIsChangeRolesOpen(true);
    };

    const handleCloseChangeRolesDialog = () => {
        setIsChangeRolesOpen(false);
        setSelectedUser(null);
    };

    const handleRolesSaved = async () => {
        await fetchInstitutionUsers(pageInfo.currentPage);
        handleCloseChangeRolesDialog();
    };

    const handleOpenBankingDialog = (user: User) => {
        setSelectedUser(user);
        setIsBankingDialogOpen(true);
    };

    const handleCloseBankingDialog = () => {
        setIsBankingDialogOpen(false);
        setSelectedUser(null);
    };

    const handleOpenResetPasswordModal = (user: User) => {
        setSelectedUser(user);
        setIsResetPasswordModalOpen(true);
    };

    const handleCloseResetPasswordModal = () => {
        setIsResetPasswordModalOpen(false);
        setSelectedUser(null);
    };

    const handleResetPassword = async () => {
        if (!selectedUser) return;

        try {
            await UserService.resetUserPassword(selectedUser.id);
            toast.showSuccess('Password has been reset and sent to admin email. Please change password after login.');
            handleCloseResetPasswordModal();
        } catch (error) {
            console.error('Error resetting password:', error);
            toast.showApiError(error, 'Failed to reset password');
        }
    };

    const handleOpenTwoFactorResetModal = (user: User) => {
        setSelectedUser(user);
        setIsTwoFactorResetModalOpen(true);
    };

    const handleCloseTwoFactorResetModal = () => {
        setIsTwoFactorResetModalOpen(false);
        setSelectedUser(null);
    };

    const handleResetTwoFactor = async () => {
        if (!selectedUser) return;
        try {
            await UserService.resetTwoFactor(selectedUser.id);
            toast.showSuccess('Two-factor authentication has been reset for this user');
            handleCloseTwoFactorResetModal();
        } catch (error) {
            toast.showApiError(error, 'Failed to reset two-factor authentication');
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchInstitutionUsers(page - 1); // API uses 0-based indexing
    };

    if (loading) return <div data-testid="loading-state">Loading...</div>;

    const rowActions = (user: User) => {
        const active = user.isActive !== false;
        const actions: { key: string; label: string; ariaLabel: string; helpKey: string; color: 'success' | 'default' | 'primary' | 'error' | 'secondary'; icon: React.ReactNode; onClick: () => void }[] = [];
        if (isGrantee(user)) {
            // Account-level actions belong to the home company; here only the grant itself can be managed.
            if (canChangeRoles) {
                actions.push({ key: 'roles', helpKey: 'users.list.action.roles', label: 'Change roles', ariaLabel: 'Change roles', color: 'primary', icon: <ManageAccounts />, onClick: () => handleOpenChangeRolesDialog(user) });
                if (user.grantMembershipId) {
                    actions.push({ key: 'revoke', helpKey: 'users.list.action.revoke', label: 'Remove group access', ariaLabel: 'Remove group access', color: 'error', icon: <Delete />, onClick: () => setRevokeTarget(user) });
                }
            }
            return actions;
        }
        if (canManageUsers) {
            actions.push({ key: 'toggle', helpKey: 'users.list.action.toggle', label: active ? 'Deactivate User' : 'Activate User', ariaLabel: active ? 'Deactivate User' : 'Activate User', color: active ? 'success' : 'default', icon: active ? <ToggleOn /> : <ToggleOff />, onClick: () => setToggleTarget(user) });
        }
        if (canChangeRoles) {
            actions.push({ key: 'roles', helpKey: 'users.list.action.roles', label: 'Change roles', ariaLabel: 'Change roles', color: 'primary', icon: <ManageAccounts />, onClick: () => handleOpenChangeRolesDialog(user) });
        }
        if (isSystemAdmin) {
            actions.push({ key: 'delete', helpKey: 'users.list.action.delete', label: 'Delete User', ariaLabel: 'Delete User', color: 'error', icon: <Delete />, onClick: () => handleOpenDeleteModal(user) });
        }
        if (canManageUsers) {
            actions.push({ key: 'reset', helpKey: 'users.list.action.resetPassword', label: 'Reset Password', ariaLabel: 'Reset Password', color: 'secondary', icon: <LockReset />, onClick: () => handleOpenResetPasswordModal(user) });
        }
        if (canReset2fa) {
            actions.push({ key: '2fa', helpKey: 'users.list.action.reset2fa', label: 'Reset Two-Factor Authentication', ariaLabel: 'Reset Two-Factor Authentication', color: 'primary', icon: <PhonelinkLock />, onClick: () => handleOpenTwoFactorResetModal(user) });
        }
        // never on your own row, and not on an admin's row unless you hold MANAGE_ALL (the backend refuses both anyway)
        const isOwnRow = !!user.username && user.username === AuthService.getCurrentUser()?.username;
        const isAdminRow = (user.roles ?? []).some(r => r.isAdmin);
        if (canManageBoards && !isOwnRow && (canManageBoardsAll || !isAdminRow)) {
            actions.push({ key: 'dashboard', helpKey: 'users.list.action.dashboard', label: 'Dashboard', ariaLabel: 'Edit dashboard', color: 'primary', icon: <SpaceDashboard />, onClick: () => navigate(`/dashboard/team/${user.id}`) });
        }
        if (canManageProfile) {
            actions.push({ key: 'banking', helpKey: 'users.list.action.banking', label: 'Banking & disbursement', ariaLabel: 'Banking and disbursement', color: 'primary', icon: <AccountBalance />, onClick: () => handleOpenBankingDialog(user) });
        }
        return actions;
    };

    const renderRowActions = (user: User) => {
        const actions = rowActions(user);
        if (actions.length === 0) return null;
        // Phone: six wrapped icons made each row ~240px tall, so collapse them into one menu.
        if (isNarrow) {
            return (
                <IconButton
                    size="small"
                    aria-label={`More actions for ${user.fullName || displayUsername(user.username)}`}
                    aria-haspopup="menu"
                    data-help="users.list.action.more"
                    onClick={(e) => setActionMenu({ anchor: e.currentTarget, user })}
                >
                    <MoreVert />
                </IconButton>
            );
        }
        return (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {actions.map(a => (
                    <Tooltip key={a.key} title={a.label} arrow>
                        <IconButton size="small" onClick={a.onClick} color={a.color} aria-label={a.ariaLabel} {...helpElement(a.helpKey)}>
                            {a.icon}
                        </IconButton>
                    </Tooltip>
                ))}
            </Stack>
        );
    };

    return (
        <div style={{ padding: '20px' }} data-help-page="users.list.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>User List</h2>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    {seatMeterMatchesViewedInstitution && <SeatMeter />}
                    {canBulkImport && (
                        <Button
                            variant="outlined"
                            startIcon={<UploadFile />}
                            data-help="users.list.bulkImport"
                            onClick={() => setIsBulkOnboardingOpen(true)}
                        >
                            Bulk Import
                        </Button>
                    )}
                    {canManageUsers && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            data-help="users.list.create"
                            onClick={() => navigate('/users/create')}
                        >
                            Create User
                        </Button>
                    )}
                </Stack>
            </div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, marginBottom: '20px' }} data-help-section="users.list.filters">
                {isSystemAdmin && (
                    <Autocomplete
                        data-help="users.list.institutionFilter"
                        options={institutions}
                        getOptionLabel={(option) => option.name}
                        value={selectedInstitution}
                        onChange={(_e, newValue) => setSelectedInstitution(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Filter by Institution" size="small" />
                        )}
                        sx={{ flex: '1 1 240px', maxWidth: 360 }}
                    />
                )}
                <TextField
                    label="Search by username"
                    data-help="users.list.search"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: '1 1 220px', maxWidth: 360 }}
                />
                <span data-help="users.list.total">Total Users: {pageInfo.totalElements}</span>
            </Box>
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isNarrow ? 0 : 640 }} data-help-section="users.list.table">
                <thead>
                    <tr>
                        <th style={tableHeaderStyle} data-help="users.list.col.name">Name</th>
                        <th style={tableHeaderStyle} data-help="users.list.col.username">Username</th>
                        {!isNarrow && <th style={tableHeaderStyle} data-help="users.list.col.phone">Phone</th>}
                        <th style={tableHeaderStyle} data-help="users.list.col.roles">Roles</th>
                        <th style={tableHeaderStyle} data-help="users.list.col.status">Status</th>
                        <th style={tableHeaderStyle} data-help="users.list.col.actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.username} style={createdUsername && user.username === createdUsername ? { backgroundColor: '#e8f5e9' } : undefined}>
                            <td style={tableCellStyle}>
                                {user.fullName || '—'}
                                {isGrantee(user) && (
                                    <Chip
                                        data-help="users.list.groupMember"
                                        size="small"
                                        variant="outlined"
                                        sx={{ ml: 1 }}
                                        label={`Group member from ${user.grantedFromInstitutionCode || user.grantedFromInstitutionName}`}
                                        title={user.grantedFromInstitutionName ?? undefined}
                                    />
                                )}
                            </td>
                            <td style={{ ...tableCellStyle, color: '#666', fontSize: '0.85em' }}>{displayUsername(user.username)}</td>
                            {!isNarrow && <td style={tableCellStyle}>{isGrantee(user) ? '—' : user.phone}</td>}
                            <td style={tableCellStyle}>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {(user.roles ?? []).map(r => (
                                        <Chip key={r.id} size="small" label={r.name} color={r.isAdmin ? 'primary' : 'default'} />
                                    ))}
                                </Stack>
                            </td>
                            <td style={tableCellStyle}>
                                {isGrantee(user) ? '—' : (
                                    <span style={{ color: user.isActive !== false ? 'green' : 'red' }}>
                                        {user.isActive !== false ? 'Active' : 'Inactive'}
                                    </span>
                                )}
                            </td>
                            <td style={tableCellStyle}>
                                {renderRowActions(user)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </ScrollableTable>
            <Menu
                anchorEl={actionMenu?.anchor ?? null}
                open={Boolean(actionMenu)}
                onClose={() => setActionMenu(null)}
            >
                {actionMenu && rowActions(actionMenu.user).map(a => (
                    <MenuItem
                        key={a.key}
                        {...helpElement(a.helpKey)}
                        onClick={() => {
                            const run = a.onClick;
                            setActionMenu(null);
                            run();
                        }}
                    >
                        <ListItemIcon>{a.icon}</ListItemIcon>
                        <ListItemText>{a.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        data-help="users.list.paging"
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Activate / deactivate confirmation */}
            <Modal open={toggleTarget !== null} onClose={() => setToggleTarget(null)} aria-labelledby="toggle-user-title">
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 'min(400px, calc(100vw - 32px))', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
                }} data-help-page="users.list.toggleDialog.page">
                    <h3 id="toggle-user-title">
                        {toggleTarget?.isActive !== false ? 'Deactivate user' : 'Activate user'}
                    </h3>
                    <p>
                        {toggleTarget?.isActive !== false
                            ? `Deactivate "${toggleTarget?.fullName || displayUsername(toggleTarget?.username)}"? They will no longer be able to sign in, and their seat is freed.`
                            : `Activate "${toggleTarget?.fullName || displayUsername(toggleTarget?.username)}"? They will be able to sign in again.`}
                    </p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => setToggleTarget(null)} variant="outlined" data-help="users.list.toggleDialog.cancel">Cancel</Button>
                        <Button
                            onClick={async () => {
                                const target = toggleTarget;
                                setToggleTarget(null);
                                if (target) await handleToggleActive(target);
                            }}
                            variant="contained"
                            data-help="users.list.toggleDialog.confirm"
                            color={toggleTarget?.isActive !== false ? 'error' : 'primary'}
                        >
                            {toggleTarget?.isActive !== false ? 'Deactivate' : 'Activate'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={revokeTarget !== null} onClose={() => setRevokeTarget(null)} aria-labelledby="revoke-grant-title">
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 'min(400px, calc(100vw - 32px))', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
                }} data-help-page="users.list.revokeDialog.page">
                    <h3 id="revoke-grant-title">Remove group access</h3>
                    <p>
                        Remove "{revokeTarget?.fullName || displayUsername(revokeTarget?.username)}" from this company? Their
                        account stays with {revokeTarget?.grantedFromInstitutionName || 'their home company'}; only the access given here is withdrawn.
                    </p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={() => setRevokeTarget(null)} variant="outlined" data-help="users.list.revokeDialog.cancel">Cancel</Button>
                        <Button
                            data-help="users.list.revokeDialog.confirm"
                            color="error"
                            variant="contained"
                            onClick={async () => {
                                const target = revokeTarget;
                                setRevokeTarget(null);
                                if (!target?.grantMembershipId || !rolesInstitutionId) return;
                                try {
                                    await GroupAccessService.revoke(rolesInstitutionId, target.grantMembershipId);
                                    toast.showSuccess('Group access removed');
                                    fetchInstitutionUsers(0);
                                } catch (e) {
                                    toast.showApiError(e, 'Failed to remove group access');
                                }
                            }}
                        >
                            Remove
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                aria-describedby="delete-user-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }} data-help-page="users.list.deleteDialog.page">
                    <h3>Delete User</h3>
                    <p>Are you sure you want to delete user "{displayUsername(selectedUser?.username)}"?</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDeleteModal} variant="outlined" data-help="users.list.deleteDialog.cancel">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error" data-help="users.list.deleteDialog.confirm">
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <ChangeRolesDialog
                open={isChangeRolesOpen}
                institutionId={rolesInstitutionId || ''}
                user={selectedUser ? { id: selectedUser.id, username: selectedUser.username, roles: selectedUser.roles ?? [] } : null}
                onClose={handleCloseChangeRolesDialog}
                onSaved={handleRolesSaved}
            />

            {selectedUser && (
                <EmployeeBankingDialog
                    open={isBankingDialogOpen}
                    userId={selectedUser.id}
                    displayName={displayUsername(selectedUser.username)}
                    onClose={handleCloseBankingDialog}
                    onSaved={() => fetchInstitutionUsers(pageInfo.currentPage)}
                />
            )}

            {canBulkImport && (
                <BulkOnboardingDialog
                    open={isBulkOnboardingOpen}
                    institutionId={rolesInstitutionId as string}
                    onClose={() => setIsBulkOnboardingOpen(false)}
                    onImported={() => fetchInstitutionUsers(pageInfo.currentPage)}
                />
            )}

            {/* Reset Password Modal */}
            <Modal
                open={isResetPasswordModalOpen}
                onClose={handleCloseResetPasswordModal}
                aria-describedby="reset-password-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }} data-help-page="users.list.resetPasswordDialog.page">
                    <h3>Reset Password</h3>
                    <p>Are you sure you want to reset the password for user "{displayUsername(selectedUser?.username)}"?</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseResetPasswordModal} variant="outlined" data-help="users.list.resetPasswordDialog.cancel">
                            Cancel
                        </Button>
                        <Button onClick={handleResetPassword} variant="contained" color="secondary" data-help="users.list.resetPasswordDialog.confirm">
                            Reset Password
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Two-Factor Reset Modal */}
            <Modal
                open={isTwoFactorResetModalOpen}
                onClose={handleCloseTwoFactorResetModal}
                aria-describedby="reset-2fa-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }} data-help-page="users.list.reset2faDialog.page">
                    <h3>Reset Two-Factor Authentication</h3>
                    <p>
                        This will disable two-factor authentication for "{displayUsername(selectedUser?.username)}"
                        and delete their backup codes. They will need to set it up again.
                    </p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTwoFactorResetModal} variant="outlined" data-help="users.list.reset2faDialog.cancel">
                            Cancel
                        </Button>
                        <Button onClick={handleResetTwoFactor} variant="contained" color="error" data-help="users.list.reset2faDialog.confirm">
                            Reset
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default UserList;
